import React, {Fragment, ReactNode} from 'react';

import {Add} from '@material-ui/icons';
import CachedIcon from '@material-ui/icons/Cached';
import EfeTabpanel from 'components/efe-tabpanel/EfeTabpanel';
import EfeTabpanelValues from 'commons/models/components/EfeTabpanelValues';
import {Box, Grid, Typography} from '@material-ui/core';
import {EfeAutocomplete, EfeButtonPrimary, EfeLink, EfeModal} from 'components';
import {useSpinner} from 'hooks';
import {CONSIGNEE_NAME, Constants, Messages, Roles, SnackbarVariants} from 'commons/constants';
import {useSnackbar} from 'notistack';
import {
    DashboardUsersValues,
    EfeCatalogValue,
    EfeResponse,
    SecurityUsersResponse,
} from 'commons/models';
import {ClientService, DocumentServices} from 'services';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as DownloadIcon} from 'assets/icons/section/action/download.svg';
import EfeTable from 'components/efe-table/EfeTable';
import DashboardUsersForm from './dashboard-users-form/DashboardUsersForm';
import {USERS} from './UsersTable';
import {Utils} from 'commons';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {ConsigneeCatalog} from 'commons/types/Types';
import {Generate} from 'commons/utils/Functions';
import {getConsigneeValidationError} from 'commons/utils/Messagefunctions';
import ConsigneeSignature from '../consignee-signature/ConsigneeSignature';

type Props = {
    view: ReactNode;
    page: number;
    rowsPerPage: number;
    users?: SecurityUsersResponse;
    handleChangePage: (page: number) => void;
    handleChangeRowsPerPage: (perPage: number) => void;
    getUsers: (userRole: string, status?: string, personKey?: string) => void;
};

const tabData: Array<EfeTabpanelValues> = [
    {
        label: 'Todos',
        tabIndex: 0,
        indexValue: undefined,
        view: '',
        icon: <CachedIcon />,
    },
    {
        label: 'Activos',
        tabIndex: 1,
        indexValue: 'A',
        view: '',
    },
    {
        label: 'Inactivos',
        tabIndex: 2,
        indexValue: 'I',
        view: '',
    },
];
/* eslint-disable react/jsx-handler-names */
function transformToCatalog(data: Array<ConsigneeCatalog>): Array<EfeCatalogValue> {
    const catalog: Array<EfeCatalogValue> = [];
    data.forEach((item) =>
        catalog.push({
            id: item.personKey,
            secondaryId: item.personKey,
            order: item.order,
            value: `${item.roleDescription}: ${item.personKey} | ${item.fullName ? item.fullName : item.personKey}`,
            status: 'A',
        })
    );
    return catalog;
}

function validateLoadUser(loadUser: ConsigneeCatalog): boolean {
    return (
        loadUser &&
        (Roles.CLIENT === loadUser.role ||
            (loadUser.swTermsConditions && loadUser.swRegisteredEfectivale))
    );
}

export default function DashboardUsers(props: Props) {
    const {users, getUsers, view, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage} =
        props;

    const {enqueueSnackbar} = useSnackbar();
    const {loadUsers} = useRelatedPeopleContext();
    const {expedientId, fatherKycId} = useMenuContext();

    const [open, setOpen] = React.useState<boolean>(false);
    const [tabIndex, setTabIndex] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadUser, setLoadUser] = React.useState<ConsigneeCatalog>();
    const [openSignaturePad, setOpenSignaturePad] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>(Messages.LOAD_USER_REQUIRED);

    const handleTabChange = (event: React.ChangeEvent<any>, newValue: number) => {
        if (loadUser && validateLoadUser(loadUser)) {
            setTabIndex(newValue);
            getUsers(loadUser.role, tabData[newValue].indexValue, loadUser.personKey);
        } else {
            enqueueSnackbar(message, {
                variant: SnackbarVariants.ERROR,
            });
        }
    };

    async function uploadUsers(request: DashboardUsersValues) {
        setIsLoading(true);
        try {
            const fileConverted = await (await fetch(request.file)).blob();
            const params: DashboardUsersValues = {
                file: fileConverted,
                expedientId: expedientId,
                kycFatherId: fatherKycId,
                personKey: request.personKey,
            };
            const response: EfeResponse<any> = await ClientService.uploadSecondaryClients(params);
            if (response && response.data) {
                setIsLoading(false);
                enqueueSnackbar(Messages.USERS_ARE_LOADING, {
                    variant: SnackbarVariants.SUCCESS,
                });
                setOpen(false);
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    async function handleDownload(doc: string) {
        try {
            setIsLoading(true);
            const response = await DocumentServices.getDocument(doc, 'csv');
            Utils.functions.downloadDocs(response, `${doc}`);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                variant: SnackbarVariants.SUCCESS,
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function validateConsignee(item: ConsigneeCatalog) {
        if (!item.swRegisteredEfectivale || !item.swContractSignature) {
            setMessage(getConsigneeValidationError(item.fullName));
            enqueueSnackbar(getConsigneeValidationError(item.fullName), {
                variant: SnackbarVariants.ERROR,
            });
        } else if (!item.swTermsConditions) {
            setOpenSignaturePad(true);
            setOpen(true);
        }
    }

    function handleSelectValue(id: any) {
        const items: Array<ConsigneeCatalog> = loadUsers.filter(
            (item: ConsigneeCatalog) => item.personKey === id
        );
        if (items && items.length) {
            setLoadUser({...items[0]});
            if (Roles.CLIENT != items[0].role) {
                validateConsignee(items[0]);
            }
        } else {
            setTabIndex(0);
            setLoadUser(undefined);
            setMessage(Messages.LOAD_USER_REQUIRED);
        }
    }

    function closeModal() {
        setOpen(!open);
        setOpenSignaturePad(false);
    }

    React.useEffect(() => {
        if (loadUser && validateLoadUser(loadUser)) {
            getUsers(loadUser.role, tabData[tabIndex].indexValue, loadUser.personKey);
        } else {
            getUsers('', undefined, undefined);
        }
    }, [loadUser]);

    React.useEffect(() => {
        if (loadUsers && loadUser) {
            handleSelectValue(loadUser.personKey);
        }
    }, [loadUsers]);
    return (
        <Fragment>
            {useSpinner(isLoading)}

            {open && (
                <EfeModal
                    key={Generate.uniqueID('modal')}
                    open={open}
                    title={openSignaturePad ? `Firma de ${CONSIGNEE_NAME}` : 'Carga de usuarios'}
                    sizeOverride={75}
                    reactElement={
                        openSignaturePad && loadUser ? (
                            <ConsigneeSignature
                                fatherKycId={fatherKycId}
                                loadUser={loadUser}
                                closeModal={closeModal}
                            />
                        ) : (
                            <DashboardUsersForm
                                submitHandler={uploadUsers}
                                personKey={loadUser?.personKey}
                            />
                        )
                    }
                    handleAction={closeModal}
                />
            )}
            <Grid item xs={9} sm={8}>
                <Box p={1}>
                    <Typography variant={'body1'}>
                        Selecciona una opci&oacute;n para realizar la consulta/carga de usuarios:
                    </Typography>
                </Box>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                    <EfeAutocomplete
                        key={'select-user'}
                        required={true}
                        labelText={''}
                        name={'user'}
                        id={'user'}
                        selectedValue={loadUser?.personKey}
                        options={transformToCatalog(loadUsers)}
                        onValueChange={(value: any) => handleSelectValue(value)}
                        disabled={false}
                    />
                </Grid>
            </Grid>
            {!!loadUser && view}
            <Grid item xs={12} sm={8}>
                <Box pt={8}>
                    <EfeLink
                        bold={true}
                        variant='body1'
                        color='secondary'
                        underline='always'
                        onClick={() => {
                            handleDownload(Constants.DOC_LAYOUT_SECONDARY_CLIENT);
                        }}
                        content={
                            <Box display='flex' component='div' justifyContent='flex-start'>
                                <Box m={2}>
                                    <DownloadIcon />
                                </Box>
                                <Box m={2}>
                                    {'Formato de Carga de Clientes Titulares de Tarjeta'}
                                </Box>
                            </Box>
                        }
                    />
                </Box>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                    <Box pt={8}>
                        <EfeButtonPrimary
                            onClick={() => setOpen(!open)}
                            type='submit'
                            disabled={!loadUser || !validateLoadUser(loadUser)}
                            startIcon={<Add />}>
                            {'Agregar usuarios'}
                        </EfeButtonPrimary>
                    </Box>
                </Grid>
            </Grid>
            <EfeTabpanel data={tabData} tabIndex={tabIndex} handleTabChange={handleTabChange} />
            {users && users.data && users.pagination && (
                <>
                    <Box>
                        <EfeTable
                            title={'Usuarios'}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            data={users.data}
                            totalElements={users.pagination.totalElements}
                            columns={USERS}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Box>
                </>
            )}
        </Fragment>
    );
}
